import { useState } from "react";
import { Search, Ticket, Calendar, Mail } from "lucide-react";
import { listReservations } from "@/lib/api";
import { Link } from "react-router-dom";

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

export default function MyReservationsPage() {
  const [email, setEmail] = useState("");
  const [items, setItems] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const data = await listReservations(email);
      setItems(data);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="my-reservations-page" className="max-w-5xl mx-auto px-6 md:px-12 pt-16 pb-24">
      <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">◆ Lookup</p>
      <h1 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-[0.95] mb-3">
        Your <span className="text-[#B4FF39]">tickets.</span>
      </h1>
      <p className="text-zinc-400 max-w-xl mb-10">
        Enter the email you used at reservation. We'll pull up every spot we're holding for you.
      </p>

      <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-2 sm:gap-0 max-w-xl mb-12">
        <div className="relative flex-1">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="lookup-email-input"
            placeholder="you@email.com"
            className="w-full bg-white/5 border border-white/10 text-white pl-11 pr-4 py-4 focus:border-[#B4FF39] outline-none placeholder:text-zinc-500 font-mono-tight text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="lookup-submit-button"
          className="bg-[#B4FF39] text-[#0A0A0A] font-bold px-6 py-4 hover:bg-[#9FE822] transition-colors uppercase tracking-[0.15em] text-xs flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? "Searching…" : (<><Search className="w-4 h-4" /> Find Tickets</>)}
        </button>
      </form>

      {searched && items.length === 0 && (
        <div data-testid="reservations-empty" className="border border-white/10 p-12 text-center">
          <Ticket className="w-8 h-8 mx-auto text-zinc-500 mb-4" />
          <p className="font-display text-2xl uppercase mb-2">No tickets yet.</p>
          <p className="text-sm text-zinc-400 mb-6">Nothing booked under that email.</p>
          <Link to="/events" className="inline-block bg-[#B4FF39] text-[#0A0A0A] px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold">
            Browse Events
          </Link>
        </div>
      )}

      {items.length > 0 && (
        <div data-testid="reservations-list" className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="border border-white/10 bg-[#121212] p-6 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-6">
                <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Reservation</p>
                <p className="font-display text-2xl uppercase leading-tight">{r.event_title}</p>
                <p className="text-xs text-zinc-400 mt-2 flex items-center gap-3">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3 h-3 text-[#B4FF39]" /> Booked {formatDate(r.created_at)}
                  </span>
                </p>
              </div>
              <div className="md:col-span-3">
                <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Quantity</p>
                <p className="font-display text-2xl">{r.quantity} × ticket{r.quantity > 1 ? "s" : ""}</p>
              </div>
              <div className="md:col-span-3 text-left md:text-right">
                <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Code</p>
                <p className="font-mono-tight text-xl text-[#B4FF39] tracking-[0.2em]">{r.confirmation_code}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
